import api from './api';
import { Customer } from '../types';
import {
  CustomerContactMethod,
  CustomerFollowUp,
  CustomerProfile,
  DuplicateCandidate,
  GoogleContactSyncRun,
  GoogleDiscoveredAccount,
  GoogleDuplicateRiskItem,
  GoogleDuplicateRiskDetail,
  GoogleDiagnosticResult,
} from './customerApi';

function normalizeGoogleRawSnapshot(rawSnapshot: any): any {
  if (!rawSnapshot) return {};
  if (typeof rawSnapshot === 'string') {
    try {
      return JSON.parse(rawSnapshot);
    } catch {
      return {};
    }
  }
  return rawSnapshot;
}

function normalizeGoogleSourceSurface(source: any, rawSnapshot?: any): string {
  return String(
    source?.sourceSurface ||
    source?.source?.sourceSurface ||
    rawSnapshot?.sourceSurface ||
    rawSnapshot?.raw?.metadata?.sources?.[0]?.type ||
    ''
  ).trim().toUpperCase();
}

function extractPreferredGooglePhotoUrl(rawSnapshot: any, source?: any): string | null {
  const normalized = normalizeGoogleRawSnapshot(rawSnapshot);
  const sourceSurface = normalizeGoogleSourceSurface(source, normalized);
  const photoEntries = [
    ...(Array.isArray(normalized?.photos) ? normalized.photos : []),
    ...(Array.isArray(normalized?.raw?.photos) ? normalized.raw.photos : []),
  ];
  const nonDefaultEntry = photoEntries.find((photo: any) => {
    const url = String(photo?.url || '').trim();
    return url && photo?.default !== true;
  });
  if (nonDefaultEntry?.url) {
    return String(nonDefaultEntry.url).trim();
  }
  const fallbackEntry = photoEntries.find((photo: any) => String(photo?.url || '').trim());
  if (fallbackEntry?.url && sourceSurface === 'CONTACT') {
    return String(fallbackEntry.url).trim();
  }
  if (fallbackEntry) {
    return null;
  }
  const fallback = String(normalized?.photoUrl || '').trim();
  return fallback || null;
}

function normalizeCustomerProfile(profile: CustomerProfile): CustomerProfile {
  const externalSources = Array.isArray(profile?.externalSources)
    ? profile.externalSources.map((source) => {
        const normalizedRawSnapshot = normalizeGoogleRawSnapshot(source?.source?.rawSnapshot);
        const normalizedPhotoUrl = extractPreferredGooglePhotoUrl(normalizedRawSnapshot, source);
        const normalizedPrimaryName = String(
          normalizedRawSnapshot?.primaryName ||
          normalizedRawSnapshot?.displayName ||
          normalizedRawSnapshot?.raw?.names?.[0]?.displayName ||
          ''
        ).trim() || null;

        return {
          ...source,
          source: source?.source
            ? {
                ...source.source,
                photoUrl: normalizedPhotoUrl,
                primaryName: normalizedPrimaryName,
                rawSnapshot: {
                  ...normalizedRawSnapshot,
                  photoUrl: normalizedPhotoUrl || normalizedRawSnapshot?.photoUrl || null,
                  primaryName: normalizedPrimaryName || normalizedRawSnapshot?.primaryName || null,
                },
              }
            : source?.source ?? null,
        };
      })
    : [];

  return {
    ...profile,
    externalSources,
  };
}

let contactsSuggestForbidden = false;
let usersSuggestForbidden = false;
let queueSuggestForbidden = false;

export type ContactSearchResult = {
  id: string;
  name: string;
  company?: string | null;
  email?: string | null;
};

export const contactsApi = {
  getAll: async (): Promise<Customer[]> => {
    const response = await api.get('/contacts');
    return response.data?.data || [];
  },

  create: async (contact: Partial<Customer>): Promise<Customer> => {
    const response = await api.post('/contacts', contact);
    return response.data?.data;
  },

  update: async (id: string, updates: Partial<Customer>): Promise<Customer> => {
    const response = await api.patch(`/contacts/${id}`, updates);
    return response.data?.data;
  },

  getContactMethods: async (id: string): Promise<CustomerContactMethod[]> => {
    const response = await api.get(`/contacts/${id}/contact-methods`);
    return response.data;
  },

  addContactMethod: async (
    id: string,
    data: { type: string; value: string; label?: string; isPrimary?: boolean }
  ): Promise<CustomerContactMethod> => {
    const response = await api.post(`/contacts/${id}/contact-methods`, data);
    return response.data;
  },

  updateContactMethod: async (
    id: string,
    methodId: string,
    data: Partial<{ type: string; value: string; label: string; isPrimary: boolean }>
  ): Promise<CustomerContactMethod> => {
    const response = await api.patch(`/contacts/${id}/contact-methods/${methodId}`, data);
    return response.data;
  },

  deleteContactMethod: async (id: string, methodId: string): Promise<void> => {
    await api.delete(`/contacts/${id}/contact-methods/${methodId}`);
  },

  getFollowUps: async (id: string): Promise<CustomerFollowUp[]> => {
    const response = await api.get(`/contacts/${id}/follow-ups`);
    return response.data;
  },

  getMyFollowUps: async (): Promise<
    (CustomerFollowUp & { customer: { id: string; name: string; company?: string; status: string } })[]
  > => {
    const response = await api.get('/contacts/follow-ups/mine');
    return response.data;
  },

  addFollowUp: async (
    id: string,
    data: { note: string; dueAt: string; assignedUserId?: string }
  ): Promise<CustomerFollowUp> => {
    const response = await api.post(`/contacts/${id}/follow-ups`, data);
    return response.data;
  },

  updateFollowUp: async (
    id: string,
    followUpId: string,
    data: Partial<{ note: string; dueAt: string; assignedUserId: string; completedAt: string | null }>
  ): Promise<CustomerFollowUp> => {
    const response = await api.patch(`/contacts/${id}/follow-ups/${followUpId}`, data);
    return response.data;
  },

  deleteFollowUp: async (id: string, followUpId: string): Promise<void> => {
    await api.delete(`/contacts/${id}/follow-ups/${followUpId}`);
  },

  getProfile: async (id: string): Promise<CustomerProfile> => {
    const response = await api.get(`/contacts/${id}/profile`);
    return normalizeCustomerProfile(response.data);
  },

  getGoogleAccounts: async (): Promise<GoogleDiscoveredAccount[]> => {
    const response = await api.get('/contacts/google/accounts');
    return response.data?.data?.accounts || [];
  },

  syncGoogleContacts: async (data?: { dryRun?: boolean; sourceSurface?: 'CONTACT' | 'OTHER_CONTACT' | 'BOTH' }): Promise<GoogleContactSyncRun[]> => {
    const response = await api.post('/contacts/google/sync', data || {});
    return response.data?.data?.results || [];
  },

  syncGoogleContactsAccount: async (accountKey: string, data?: { dryRun?: boolean; sourceSurface?: 'CONTACT' | 'OTHER_CONTACT' }): Promise<GoogleContactSyncRun> => {
    const response = await api.post(`/contacts/google/sync/${encodeURIComponent(accountKey)}`, data || {});
    return response.data?.data?.result;
  },

  updateGoogleAccount: async (accountKey: string, data: { syncEnabled: boolean }): Promise<any> => {
    const response = await api.patch(`/contacts/google/accounts/${encodeURIComponent(accountKey)}`, data);
    return response.data?.data?.state;
  },

  getGoogleSyncRuns: async (params?: { limit?: number; accountKey?: string; sourceSurface?: 'CONTACT' | 'OTHER_CONTACT' }): Promise<GoogleContactSyncRun[]> => {
    const response = await api.get('/contacts/google/sync-runs', { params });
    return response.data?.data?.runs || [];
  },

  getGoogleDuplicateRisks: async (params?: { limit?: number; accountKey?: string; includeIgnored?: boolean; sourceSurface?: 'CONTACT' | 'OTHER_CONTACT' }): Promise<{ risks: GoogleDuplicateRiskItem[]; customers: any[] }> => {
    const response = await api.get('/contacts/google/duplicate-risks', { params });
    return response.data?.data || { risks: [], customers: [] };
  },

  searchGoogleDiagnostics: async (q: string): Promise<GoogleDiagnosticResult[]> => {
    const response = await api.get('/contacts/google/diagnostics/search', { params: { q } });
    return response.data?.data?.results || [];
  },

  getGoogleDuplicateRiskDetail: async (sourceId: string): Promise<GoogleDuplicateRiskDetail> => {
    const response = await api.get(`/contacts/google/duplicate-risks/${encodeURIComponent(sourceId)}`);
    return response.data?.data;
  },

  linkGoogleSourceToCustomer: async (sourceId: string, customerId: string, data?: { force?: boolean }): Promise<any> => {
    const response = await api.post(`/contacts/google/sources/${encodeURIComponent(sourceId)}/link/${encodeURIComponent(customerId)}`, data || {});
    return response.data?.data;
  },

  createCustomerFromGoogleSource: async (sourceId: string, data?: { force?: boolean }): Promise<any> => {
    const response = await api.post(`/contacts/google/sources/${encodeURIComponent(sourceId)}/create-customer`, data || {});
    return response.data?.data;
  },

  ignoreGoogleSource: async (sourceId: string, data?: { reason?: string | null }): Promise<any> => {
    const response = await api.post(`/contacts/google/sources/${encodeURIComponent(sourceId)}/ignore`, data || {});
    return response.data?.data;
  },

  unignoreGoogleSource: async (sourceId: string): Promise<any> => {
    const response = await api.post(`/contacts/google/sources/${encodeURIComponent(sourceId)}/unignore`);
    return response.data?.data;
  },

  checkDuplicates: async (params: { name?: string; phone?: string; email?: string }): Promise<DuplicateCandidate[]> => {
    const response = await api.get('/contacts/duplicates/check', { params });
    return response.data;
  },

  mergeContacts: async (
    winnerId: string,
    loserId: string
  ): Promise<{ success: boolean; winnerId: string; loserId: string; message: string }> => {
    const response = await api.post(`/contacts/${winnerId}/merge/${loserId}`);
    return response.data;
  },

  quickCreate: async (data: { name: string; phoneNumber?: string; email?: string; company?: string }): Promise<Customer> => {
    const response = await api.post('/contacts/quick-create', data);
    return response.data;
  },

  zohoPush: async (id: string): Promise<{ success: boolean; data: Customer; zohoContactId: string }> => {
    const response = await api.post(`/contacts/${id}/zoho-push`);
    return response.data;
  },

  clearImported: async (): Promise<{ deleted: number }> => {
    const response = await api.delete('/contacts/import/clear');
    return response.data;
  },

  suggest: async (q: string): Promise<Array<{ id: string; name: string; email?: string | null }>> => {
    if (!q || q.trim().length < 2) return [];
    const query = q.trim();
    try {
      const inboundResponse = await api.get('/inbound/intake/recipient-suggestions', { params: { q: query, limit: 8 } });
      const inboundRows: any[] = Array.isArray(inboundResponse?.data?.data) ? inboundResponse.data.data : [];
      if (inboundRows.length > 0) {
        return inboundRows
          .map((row: any) => ({
            id: String(row?.id || row?.email || ''),
            name: String(row?.name || '').trim() || String(row?.email || '').split('@')[0],
            email: String(row?.email || '').trim().toLowerCase(),
          }))
          .filter(row => row.email)
          .slice(0, 8);
      }
    } catch {
      // Fall through to legacy multi-source fallback
    }
    if (!contactsSuggestForbidden) {
      try {
        const response = await api.get('/contacts', { params: { search: query } });
        const data: any[] = response.data?.data || [];
        return data
          .filter((c: any) => c.email)
          .map((c: any) => ({ id: c.id, name: String(c.name || ''), email: String(c.email || '') }))
          .slice(0, 8);
      } catch (error: any) {
        const status = Number(error?.response?.status || 0);
        if (status === 401 || status === 403) {
          contactsSuggestForbidden = true;
          console.warn('[contactsApi.suggest] /contacts not permitted for this role; using fallback sources');
        }
      }
    }

    const queryLower = query.toLowerCase();
    if (!usersSuggestForbidden) {
      const queryLower = query.toLowerCase();
      try {
        const usersResponse = await api.get('/users');
        const users: any[] = Array.isArray(usersResponse?.data) ? usersResponse.data : [];
        const userResults = users
          .map((user: any) => {
            const email = String(user?.email || '').trim().toLowerCase();
            if (!email) return null;
            const name = String(user?.name || `${user?.firstName || ''} ${user?.lastName || ''}` || '')
              .replace(/\s+/g, ' ')
              .trim() || email.split('@')[0];
            return { id: String(user?.id || `user-${email}`), name, email };
          })
          .filter(Boolean) as Array<{ id: string; name: string; email: string }>;
        const matches = userResults
          .filter(item => item.email.includes(queryLower) || item.name.toLowerCase().includes(queryLower))
          .slice(0, 8);
        if (matches.length > 0) return matches;
      } catch (usersError: any) {
        const usersStatus = Number(usersError?.response?.status || 0);
        if (usersStatus === 401 || usersStatus === 403) {
          usersSuggestForbidden = true;
          console.warn('[contactsApi.suggest] /users not permitted for this role; using inbound queue fallback');
        }
      }
    }

    if (!queueSuggestForbidden) {
      try {
      const response = await api.get('/inbound/intake/queue', { params: { q: query, page: 1, pageSize: 30 } });
      const rows: any[] = response?.data?.data || [];
      const emails = new Map<string, { id: string; name: string; email: string }>();
      const parseEmails = (raw: any): string[] =>
        String(raw || '')
          .split(/[;,]/)
          .map((v: string) => v.trim())
          .filter(Boolean);
      const normalizeEmail = (value: string): string => {
        const raw = String(value || '').trim();
        if (!raw) return '';
        const match = raw.match(/<([^>]+)>/);
        return String(match?.[1] || raw).trim().replace(/^mailto:/i, '').toLowerCase();
      };
      const nameFromEmail = (email: string): string => {
        const local = String(email || '').split('@')[0] || '';
        if (!local) return 'Contact';
        return local
          .replace(/[._-]+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .split(' ')
          .map(part => (part ? `${part.charAt(0).toUpperCase()}${part.slice(1)}` : ''))
          .join(' ')
          .trim() || 'Contact';
      };
      const add = (rawEmail: string) => {
        const email = normalizeEmail(rawEmail);
        if (!email) return;
        if (emails.has(email)) return;
        emails.set(email, { id: `queue-${email}`, name: nameFromEmail(email), email });
      };
      for (const row of rows) {
        add(String(row?.fromEmail || ''));
        parseEmails(row?.toEmails).forEach(add);
      }
      const needle = query.toLowerCase();
      return Array.from(emails.values())
        .filter(item => item.email.toLowerCase().includes(needle) || item.name.toLowerCase().includes(needle))
        .slice(0, 8);
      } catch (queueError: any) {
        const queueStatus = Number(queueError?.response?.status || 0);
        if (queueStatus === 401 || queueStatus === 403) {
          queueSuggestForbidden = true;
          console.warn('[contactsApi.suggest] /inbound/intake/queue not permitted for this role; using local-only suggestions');
        }
      }
    }
    return [];
  },

  search: async (q: string): Promise<ContactSearchResult[]> => {
    const query = String(q || '').trim();
    if (query.length < 2) return [];
    const response = await api.get('/contacts', { params: { search: query } });
    const rows: any[] = Array.isArray(response?.data?.data) ? response.data.data : [];
    return rows
      .map((row: any) => ({
        id: String(row?.id || '').trim(),
        name: String(row?.name || row?.company || row?.email || '').trim(),
        company: row?.company ? String(row.company).trim() : null,
        email: row?.email ? String(row.email).trim().toLowerCase() : null,
      }))
      .filter((row) => !!row.id && !!row.name)
      .slice(0, 12);
  },
};
